import React, { createContext, useContext } from 'react';
import { SnackbarProvider, SnackbarKey, useSnackbar, VariantType } from 'notistack';
import { CustomSnackbar } from './CustomSnackbar';

export type ToastType = 'error' | 'success' | 'info' | 'warning';
export type ToastPosition = 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
export type MessageProps = {
    id?: number,
    header?: string
    body?: string,
    type?: ToastType
    count?: number
};

export type ToastMessageProps = {
    message?: string;
    header?: string;
    body?: string;
    options?: {
        variant?: VariantType;
        autoHideDuration?: number;
        preventDuplicate?: boolean;
    };
};

export type ToastMessageProviderProps = {
    success: (props: ToastMessageProps) => SnackbarKey;
    error: (props: ToastMessageProps) => SnackbarKey;
    info: (props: ToastMessageProps) => SnackbarKey;
    warning: (props: ToastMessageProps) => SnackbarKey;
    closeToast: (key: SnackbarKey) => void;
};


export const ToastMessageQueueContext = createContext<ToastMessageProviderProps>({
    success: () => '',
    error: () => '',
    info: () => '',
    warning: () => '',
    closeToast: () => {},
});

interface ToastMessageProviderComponentProps {
    children: React.ReactNode;
    maxSnack?: number;
    autoHideDuration?: number;
}

export const ToastMessageProvider = ({ 
    children, 
    maxSnack = 5, 
    autoHideDuration = 5000 
}: ToastMessageProviderComponentProps) => {
    return (
        <SnackbarProvider 
            maxSnack={maxSnack} 
            autoHideDuration={autoHideDuration}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            Components={{
                success: CustomSnackbar,
                error: CustomSnackbar,
                warning: CustomSnackbar,
                info: CustomSnackbar,
                default: CustomSnackbar,
            }}
        >
            <ToastMessageProviderContent>
                {children}
            </ToastMessageProviderContent>
        </SnackbarProvider>
    );
};

const ToastMessageProviderContent = ({ children }: { children: React.ReactNode }) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const showToast = (props: ToastMessageProps, variant: VariantType) => {
        const { message, body, header, options = {} } = props;
        // Use message if available, otherwise use body, otherwise use empty string
        const messageText = message || body || '';
        const content = header ? `${header}: ${messageText}` : messageText;
        
        if (!content) {
            console.error('enqueueSnackbar called with invalid argument: empty message');
            return '';
        }
        
        return enqueueSnackbar(content, {
            variant,
            ...options,
        });
    };

    const success = (props: ToastMessageProps) => showToast(props, 'success');
    const error = (props: ToastMessageProps) => showToast(props, 'error');
    const info = (props: ToastMessageProps) => showToast(props, 'info');
    const warning = (props: ToastMessageProps) => showToast(props, 'warning');
    const closeToast = (key: SnackbarKey) => closeSnackbar(key);

    return (
        <ToastMessageQueueContext.Provider 
            value={{
                success,
                error,
                info,
                warning,
                closeToast
            }}
        >
            {children}
        </ToastMessageQueueContext.Provider>
    );
};

export function useToastMessageQueue() {
    return useContext(ToastMessageQueueContext);
}
